'use client';

import React from 'react';
import styled from 'styled-components';
import { DensityProvider, type Density } from '@liftango/ui-tokens';

export interface FormProps extends Omit<React.FormHTMLAttributes<HTMLFormElement>, 'onSubmit'> {
  /** Form submit handler. `e.preventDefault()` is called automatically. */
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  /**
   * Density region for fields inside this form. Defaults to `comfortable` —
   * forms are action-critical surfaces and benefit from larger control
   * heights and padding than the app-default compact density.
   *
   * Pass `compact` to opt back in for inline / table-row forms.
   */
  density?: Density;
  children: React.ReactNode;
}

export interface FormFieldProps {
  /** Field identifier — used to wire `htmlFor`, `id`, and `aria-*` attributes. */
  name: string;
  /** Visible label. */
  label: string;
  /** Validation error message. When set the field enters the error state. */
  error?: string;
  /** Hint shown below the input when there is no error. */
  help?: string;
  /** Marks the field as required and renders a required indicator. */
  required?: boolean;
  children: React.ReactNode;
}

export interface FormFieldGroupProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * How the fields divide available width. Defaults to equal columns
   * (`1fr` per child). Pass explicit values like `['2fr', '1fr']` for
   * unequal splits.
   */
  columns?: string[];
  children: React.ReactNode;
}

export interface FormActionsProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Horizontal alignment of the actions. Defaults to `end`. */
  align?: 'start' | 'center' | 'end';
  children: React.ReactNode;
}

const Root = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${(p) => p.theme.spaceAlias.lg};
`;

const FieldRoot = styled.fieldset`
  display: flex;
  flex-direction: column;
  gap: ${(p) => p.theme.spaceAlias.xs};
  border: none;
  margin: 0;
  padding: 0;
  min-width: 0;
`;

const Label = styled.legend`
  font-family: ${(p) => p.theme.fontFamily.ui};
  font-size: ${(p) => p.theme.fontSize.sm};
  font-weight: ${(p) => p.theme.fontWeight.medium};
  color: ${(p) => p.theme.colors.neutral.inkSoft};
  margin-bottom: ${(p) => p.theme.spaceAlias.xs};
  display: flex;
  gap: ${(p) => p.theme.spaceAlias.xs};
`;

const Required = styled.span`
  color: ${(p) => p.theme.colors.semantic.error.color};
  font-weight: ${(p) => p.theme.fontWeight.bold};
`;

const Help = styled.p`
  margin: 0;
  font-family: ${(p) => p.theme.fontFamily.ui};
  font-size: ${(p) => p.theme.fontSize.xs};
  color: ${(p) => p.theme.colors.neutral.inkMute};
  line-height: ${(p) => p.theme.lineHeight.body};
`;

const ErrorMsg = styled.p`
  margin: 0;
  font-family: ${(p) => p.theme.fontFamily.ui};
  font-size: ${(p) => p.theme.fontSize.xs};
  color: ${(p) => p.theme.colors.semantic.error.color};
  line-height: ${(p) => p.theme.lineHeight.body};
`;

const FieldGroupRoot = styled.div<{ $cols: string }>`
  display: grid;
  grid-template-columns: ${(p) => p.$cols};
  gap: ${(p) => p.theme.spaceAlias.md};
`;

const ActionsRoot = styled.div<{ $align: string }>`
  display: flex;
  justify-content: ${(p) =>
    p.$align === 'start' ? 'flex-start' : p.$align === 'center' ? 'center' : 'flex-end'};
  gap: ${(p) => p.theme.spaceAlias.sm};
  padding-top: ${(p) => p.theme.spaceAlias.sm};
`;

/**
 * Form layout composite. Provides vertical field stacking with consistent
 * spacing, validation message slots, required indicators, and an actions row.
 *
 * State is controlled: the consumer owns values, errors, and submission
 * logic. The composite only handles layout and ARIA wiring.
 *
 * @example
 * <Form onSubmit={handleSubmit}>
 *   <Form.Field name="email" label="Email" error={errors.email} required>
 *     <TextInput value={email} onChange={setEmail} />
 *   </Form.Field>
 *   <Form.Actions>
 *     <Button kind="primary" type="submit">Save</Button>
 *   </Form.Actions>
 * </Form>
 */
export function Form({ onSubmit, density = 'comfortable', children, ...rest }: FormProps) {
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    onSubmit(e);
  };
  return (
    <Root onSubmit={handleSubmit} {...rest}>
      <DensityProvider density={density}>{children}</DensityProvider>
    </Root>
  );
}

/**
 * A labelled form field with optional error and help text. The child input
 * is associated to the label via `htmlFor`/`id`.
 */
function Field({ name, label, error, help, required, children }: FormFieldProps) {
  return (
    <FieldRoot role="group" aria-required={required || undefined}>
      <Label>
        {label}
        {required && <Required aria-hidden="true">*</Required>}
      </Label>
      {React.isValidElement(children)
        ? React.cloneElement(children as React.ReactElement<Record<string, unknown>>, {
            id: name,
            'aria-describedby': error ? `${name}-error` : help ? `${name}-help` : undefined,
            'aria-invalid': error ? true : undefined,
          })
        : children}
      {error && (
        <ErrorMsg id={`${name}-error`} role="alert">
          {error}
        </ErrorMsg>
      )}
      {!error && help && <Help id={`${name}-help`}>{help}</Help>}
    </FieldRoot>
  );
}

/**
 * Horizontal multi-field row. Divides available width into equal columns by
 * default. Pass `columns` to control each column's flex sizing.
 *
 * @example
 * <Form.FieldGroup columns={['2fr', '1fr']}>
 *   <Form.Field name="city" label="City">…</Form.Field>
 *   <Form.Field name="postcode" label="Postcode">…</Form.Field>
 * </Form.FieldGroup>
 */
function FieldGroup({ columns, children, ...rest }: FormFieldGroupProps) {
  const count = React.Children.count(children);
  const cols = columns ? columns.join(' ') : Array(count).fill('1fr').join(' ');
  return (
    <FieldGroupRoot $cols={cols} {...rest}>
      {children}
    </FieldGroupRoot>
  );
}

/**
 * Action button row. Right-aligned by default to match standard form
 * conventions. Change `align` for edge cases.
 */
function Actions({ align = 'end', children, ...rest }: FormActionsProps) {
  return (
    <ActionsRoot $align={align} {...rest}>
      {children}
    </ActionsRoot>
  );
}

Form.Field = Field;
Form.FieldGroup = FieldGroup;
Form.Actions = Actions;
